import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, setToken, setStoredUser } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Logo } from '@/components/Logo';
import { ShieldCheck, UserPlus, Briefcase, Phone } from 'lucide-react';
import { Stepper } from '@/components/Primitives';

export default function SignupPage() {
  const [params] = useSearchParams();
  const presetRole = params.get('role') || '';
  const [step, setStep] = useState(0);
  const [role, setRole] = useState(presetRole);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', city: '', commune: '' });
  const [cities, setCities] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const { loginWith, refreshMe } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get('/catalog/cities').then((r) => setCities(r.data.items || [])); }, []);
  useEffect(() => {
    const c = cities.find((x) => x.name === form.city);
    setCommunes(c?.communes || []);
  }, [form.city, cities]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const doSignup = async () => {
    if (!role) return toast.error('Choisissez votre profil');
    if (!form.email || !form.password || !form.first_name || !form.last_name || !form.phone) return toast.error('Champs requis manquants');
    if (form.password.length < 6) return toast.error('Mot de passe: min 6 caractères');
    setLoading(true);
    try {
      const r = await api.post('/auth/signup', { ...form, role });
      loginWith(r.data.token, r.data.user);
      setSmsCode(r.data.sms_code_demo || '');
      toast.success('Compte créé avec succès');
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l’inscription');
    } finally { setLoading(false); }
  };

  const doVerifyPhone = async () => {
    try {
      await api.post(`/auth/verify-phone?code=${codeInput}`);
      await refreshMe();
      toast.success('Téléphone vérifié');
      nav(role === 'jobeur' ? '/verify-id' : '/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code incorrect');
    }
  };

  const steps = ['Profil', 'Informations', 'Vérification'];

  return (
    <div className="min-h-screen bg-[#F7FAFF] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="flex flex-col items-center mb-6">
          <Logo size="xl" variant="default" />
          <p className="mt-3 text-2xl font-bold tracking-tight text-[#0E3F80]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Job<span className="text-[#1677F2]">Kongo</span>
          </p>
          <p className="text-xs text-[#16764A] font-medium mt-0.5">Mosala na confiance</p>
        </div>
        <Card className="p-6 md:p-8">
          <Stepper steps={steps} current={step} />
          {step === 0 && (
            <div className="mt-8 space-y-4">
              <div>
                <h1 className="text-2xl font-bold text-[#0B1220]">Vous êtes…</h1>
                <p className="text-sm text-slate-600">Choisissez votre profil pour commencer.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { id: 'employeur', icon: UserPlus, title: 'Employeur', desc: 'J’ai besoin d’un service à domicile.' },
                  { id: 'jobeur', icon: Briefcase, title: 'Jobeur', desc: 'Je propose mes services à la communauté.' },
                ].map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => setRole(o.id)}
                    data-testid={`signup-role-${o.id}`}
                    className={`text-left p-4 rounded-[var(--radius)] border-2 ${role === o.id ? 'border-[#1677F2] bg-[#F2F8FF]' : 'border-[#E2E8F0] hover:border-[#C7E2FF]'}`}
                  >
                    <o.icon size={20} className="text-[#1677F2]" />
                    <p className="font-semibold mt-2">{o.title}</p>
                    <p className="text-xs text-slate-600 mt-0.5">{o.desc}</p>
                  </button>
                ))}
              </div>
              <Button
                className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]"
                disabled={!role}
                onClick={() => setStep(1)}
                data-testid="signup-next-step-0"
              >
                Continuer
              </Button>
              <p className="text-center text-sm text-slate-600">
                Déjà un compte ? <a href="/login" className="text-[#1677F2] font-medium">Connectez-vous</a>
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="mt-8 space-y-4">
              <h1 className="text-2xl font-bold text-[#0B1220]">Vos informations</h1>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom</Label><Input value={form.first_name} onChange={(e) => update('first_name', e.target.value)} data-testid="signup-first-name" /></div>
                <div><Label>Nom</Label><Input value={form.last_name} onChange={(e) => update('last_name', e.target.value)} data-testid="signup-last-name" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} data-testid="signup-email" /></div>
              <div><Label>Téléphone (+243...)</Label><Input value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+243..." data-testid="signup-phone" /></div>
              <div><Label>Mot de passe (6 caractères min.)</Label><Input type="password" value={form.password} onChange={(e) => update('password', e.target.value)} data-testid="signup-password" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ville</Label>
                  <select className="mt-1 h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={form.city} onChange={(e) => update('city', e.target.value)} data-testid="signup-city">
                    <option value="">Choisir…</option>
                    {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Commune</Label>
                  <select className="mt-1 h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm" value={form.commune} onChange={(e) => update('commune', e.target.value)} data-testid="signup-commune">
                    <option value="">Choisir…</option>
                    {communes.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(0)} className="flex-1">Retour</Button>
                <Button onClick={doSignup} disabled={loading} className="flex-1 bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="signup-submit-button">
                  {loading ? 'Création…' : 'Créer mon compte'}
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-2 text-[#16764A]">
                <ShieldCheck size={20} /> <span className="font-semibold">Compte créé</span>
              </div>
              <h1 className="text-2xl font-bold">Vérifiez votre téléphone</h1>
              <p className="text-sm text-slate-600">
                Un code à 6 chiffres a été généré pour démo. En V1, il s&apos;affiche ci-dessous. En production, il sera envoyé par SMS.
              </p>
              {smsCode && (
                <div className="bg-[#FFF6C7] border border-[#F7D618]/60 rounded-[var(--radius)] p-3 flex items-center gap-3">
                  <Phone className="text-[#7A5F00]" size={18} />
                  <div>
                    <p className="text-xs text-[#5C4600]">Code SMS (demo V1)</p>
                    <p className="font-mono-num text-2xl font-bold text-[#5C4600]" data-testid="signup-sms-code-demo">{smsCode}</p>
                  </div>
                </div>
              )}
              <div>
                <Label>Entrez le code</Label>
                <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value)} maxLength={6} data-testid="signup-sms-input" />
              </div>
              <Button onClick={doVerifyPhone} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="signup-verify-phone-btn">Vérifier</Button>
              <button onClick={async () => {
                const r = await api.post('/auth/resend-sms');
                setSmsCode(r.data.sms_code_demo);
              }} className="w-full text-xs text-slate-600 underline">Renvoyer un nouveau code</button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
